import { useTheme } from "@emotion/react";
import { Box, Button, IconButton, Tooltip, TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import BlockIcon from "@mui/icons-material/Block";
import { tokens } from "../../../theme";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "../../global/Loader";
import { pageSizeOptions, pageSize, page } from "../../../utils/paginationConfig";
import { DataGrid } from "@mui/x-data-grid";
import { CustomGridToolbar } from "../../../utils/dataGridCustom"; 
import { getUserData } from "../../../services/staff-services/centerService";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { SetCurrentPageTopic } from "../../../store/action/headerChange";
import { saveAs } from "file-saver";
import { escapeCSVField } from "../../../utils/utils";

const Users = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const dispatch = useDispatch();
  dispatch(SetCurrentPageTopic("User Management"));

  const [paginationModel, setPaginationModel] = useState({page: page, pageSize: pageSize});
  const [rowCount, setRowCount] = useState(0);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const headerData = ["Name", "Email", "Center Code", "Phone", "Address"];

  const columns = [
    {
      field: "name",
      headerName: headerData[0],
      flex: 4,
      renderCell: (params) => (
        <Box style={{ textTransform: "uppercase" }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "email",
      headerName: headerData[1],
      flex: 3,
      cellClassName: "name-column--cell",
    },
    {
      field: "centerCode",
      headerName: headerData[2],
      flex: 2,
      cellClassName: "name-column--cell",
    },
    {
      field: "phoneNumber",
      headerName: headerData[3],
      flex: 2,
      cellClassName: "name-column--cell",
    },
    {
      field: "address",
      headerName: headerData[4],
      flex: 5,
      cellClassName: "name-column--cell",
    },
    {
      field: "action",
      headerName: "Actions",
      flex: 2,
      cellClassName: "name-column--cell",
      renderCell: (params) => (
        <Box display="flex" gap={0.1}>
          <Tooltip title="Edit">
            <IconButton color="info" onClick={() => handleEdit(params.row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Change Password">
            <IconButton
              color="warning"
              onClick={() => handleChangePassword(params.row)}
            >
              <LockResetIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Block">
            <IconButton
              color="error"
              onClick={() => handleBlockUser(params.row)}
            >
              <BlockIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const response = await handleTableData(page, pageSize);
    };
    fetchData();
  }, []);

  const handleTableData = async (page = paginationModel.page, limit = paginationModel.pageSize, search = searchTerm) => {
    setIsLoading(true);
    setPaginationModel({page: page, pageSize: limit});

    try {
      const obj = {page, limit, search};
      const response = await getUserData(obj);
      setData(response.data.data);
      setRowCount(response.data.totalCount ?? 0);
    } catch (error) {
      console.log(error);
      toast.error("Please refresh");
    }

    setIsLoading(false);
  };

  const onClickSearch = () => {
    handleTableData(page,pageSize, searchTerm);
  };

  const handleEdit = (user) => {
    console.log("Edit user:", user);
    // Navigate to edit form or open modal
  };

  const handleChangePassword = (user) => {
    console.log("Change password for:", user);
    // Show change password modal
  };

  const handleBlockUser = (user) => {
    console.log("Block user:", user);
    // Confirm and call API to block
  };

  const handlePaginationModelChange = (newModel) => {
    const resetPage = newModel.pageSize !== paginationModel.pageSize;
    const updatedModel = {
      ...newModel,
      page: resetPage ? 0 : newModel.page,
    };

    setPaginationModel(updatedModel);
    handleTableData(updatedModel.page,updatedModel.pageSize, searchTerm);
  };

  const handleExportCSV = async () => {
    try {
      const response = await getUserData({
        page: 0,
        limit: rowCount,
        search: searchTerm
      });
  
      const data = response.data.data;
      const csvRows = data.map(row =>
        [escapeCSVField(row.name),
          escapeCSVField(row.email),
          escapeCSVField(row.centerCode),
          escapeCSVField(row.phoneNumber),
          escapeCSVField(row.address)
        ].join(",")
      );
  
      const csvContent = [headerData.join(","), ...csvRows].join("\n");
  
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, `user_list.csv`);
    } catch (error) {
      console.error("CSV Export Failed:", error);
      toast.error("Failed to export CSV.");
    }
   };

  const handleAddUser = () => {
    navigate("/dp-staff-dashboard/add-center-manager");
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box m="0 20px">
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ display: "flex", flex: 1, gap: 1 }}>
          <TextField
            variant="filled"
            label="Search user"
            type="text"
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
            sx={{ width: 300 }}
          />

          <Button
            variant="contained"
            onClick={onClickSearch}
            sx={{ background: "#3498db", height: "50px", whiteSpace: "nowrap" }}
          >
            Search
          </Button>
        </Box>

        <Box>
          <Button
            variant="contained"
            onClick={handleAddUser}
            sx={{ background: "#70d8bd", height: "50px", whiteSpace: "nowrap" }}
          >
            Add Center Manager
          </Button>
        </Box>
      </Box>

      <Box
        display="grid"
        width="100%"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            fontSize: "15px",
          },
          "& .name-column--cell": {
            color: colors.primary[100],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            border: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.redAccent[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={data}
          getRowId={(row) => row.centerInChargeCode}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={pageSizeOptions}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          loading={isLoading}
          components={{ Toolbar: CustomGridToolbar }}
          componentsProps={{
            toolbar: {
              onExport: handleExportCSV,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Users;
